// The Approvals card where a call stopped (02-DESIGN/14-solutions/
// 03-the-console.md § the words; 02-DESIGN/19-chat/01-the-chat-solution.md
// § an escalated turn; 03-the-guardrails-service.md § Approvals; hq decision
// 0083 § 5, § 6): the crossing's id read from the refusal's
// Impire-Guard-Escalation header, and where the card stands as the console
// reads the crossing again — open, decided here or elsewhere, run out of
// time, or addressed to admins the person cannot read. Pure, so that the
// card's course is tested without a connection.

import { envelope, guard } from "@impire-io/sdk";

import { isRefusal } from "../../platform/bus.ts";
import { Decision, Status, type Decision as DecisionT, type Record_ } from "../../wire/guard.ts";

/** POLL is how often, in milliseconds, an open card reads its crossing again. */
export const POLL = 4_000;

/**
 * serviceErrorOf is the service's own refusal under what a call threw: the
 * bus keeps it as its refusal's cause, the frame reader throws it bare.
 */
export function serviceErrorOf(err: unknown): envelope.ServiceError | undefined {
  const inner = isRefusal(err) ? err.cause : err;
  return inner instanceof envelope.ServiceError ? inner : undefined;
}

/** escalationOf is the crossing's id a `guard-escalated` refusal carries, read by the library from its headers; undefined for any other failure. */
export function escalationOf(err: unknown): string | undefined {
  return guard.escalationOf(serviceErrorOf(err));
}

/** headerOf reads one of a refusal's headers, as the reply carried them; empty where absent. */
export function headerOf(err: unknown, name: string): string {
  return serviceErrorOf(err)?.headers?.get(name) ?? "";
}

/** Card is where the card stands. */
export type Card =
  | { kind: "reading" }
  | { kind: "open"; record: Record_ }
  /** Addressed to admins, and this person cannot read it: they wait for an admin. */
  | { kind: "admin" }
  | { kind: "failed"; err: unknown }
  | { kind: "granted"; record?: Record_ }
  | { kind: "denied"; record?: Record_ }
  | { kind: "expired"; record?: Record_ }
  | { kind: "withdrawn"; record?: Record_ };

/** CardEvent is what moves it: a read of the crossing, a read that found none, a read that failed, the person's own decision. */
export type CardEvent =
  | { kind: "read"; record: Record_ }
  | { kind: "unreadable" }
  | { kind: "failed"; err: unknown }
  | { kind: "resolved"; decision: DecisionT };

export const reading: Card = { kind: "reading" };

/** ended reports whether the card is decided: nothing moves it after. */
export function ended(c: Card): boolean {
  return c.kind === "granted" || c.kind === "denied" || c.kind === "expired" || c.kind === "withdrawn";
}

/** polls reports whether the card reads its crossing again: while it waits on a decision it can see. */
export function polls(c: Card): boolean {
  return c.kind === "reading" || c.kind === "open" || c.kind === "failed";
}

function fromRecord(r: Record_, now: number): Card {
  switch (r.status) {
    case Status.granted:
      return { kind: "granted", record: r };
    case Status.denied:
      return { kind: "denied", record: r };
    case Status.expired:
      return { kind: "expired", record: r };
    case Status.revoked:
      return { kind: "withdrawn", record: r };
    default: {
      const at = Date.parse(r.expires);
      return Number.isFinite(at) && at <= now ? { kind: "expired", record: r } : { kind: "open", record: r };
    }
  }
}

/** step moves the card by one event at the time now, in milliseconds. A decided card stays decided. */
export function step(c: Card, ev: CardEvent, now: number): Card {
  if (ended(c)) {
    return c;
  }
  switch (ev.kind) {
    case "read":
      return fromRecord(ev.record, now);
    case "unreadable":
      return { kind: "admin" };
    case "failed":
      // A read that failed while the card was open leaves it open: the next read may answer.
      return c.kind === "open" ? c : { kind: "failed", err: ev.err };
    case "resolved": {
      const record = c.kind === "open" ? c.record : undefined;
      return ev.decision === Decision.granted ? { kind: "granted", record } : { kind: "denied", record };
    }
  }
}

/** resends reports whether moving from prev to next is the approval the element asks again on: once, on the move into granted. */
export function resends(prev: Card, next: Card): boolean {
  return next.kind === "granted" && prev.kind !== "granted";
}
